import { get, isNil } from 'lodash';
import { ModeName } from '@commutifi/modes';
import { primaryRoutePath, REF_KEYS_LIST } from 'constants/onboarding';
import { DaysValues, Location, OptionalFrequency, Route } from 'shared/interfaces';
import { CommuteCostsProps } from '@commutifi/models/CommuteCostsProps';
import { Account, Question, QUESTION_TYPES } from '@commutifi-fe/interfaces';
import { combineFrequencies } from 'utils/routes';
import { getBuildingAnswer } from 'utils/helpers/surveys';
interface IEnterpriseSurvey {
  id: string;
  account: {
    name: string;
    email: string;
  } | undefined;
  building: {
    id: string;
    address: string;
    location: Location;
  } | undefined;
  worksite: {
    id: string;
    name: string;
  } | undefined;
  enterprise: {
    id: string;
    name: string;
  } | undefined;
  organization: {
    id: string;
    name: string;
  } | undefined;
  accountContext?: Account;
}
const filterActivityMapper = (answersById: Record<string, any> | undefined) => {
  const previousAnswers = answersById || {};
  const routeAnswer = previousAnswers[REF_KEYS_LIST.commuteProfileRoute];
  const primaryRoute = get(routeAnswer, primaryRoutePath);
  // Make sure at least the primary route is defined if we have an activity mapper question
  if (!primaryRoute) {
    return false;
  }
  return true;
};
const filterMissingStartOrEndAddress = (answersById: Record<string, any> | undefined, enterpriseSurveyData: IEnterpriseSurvey) => {
  const previousAnswers = answersById || {};
  // For home and building answers we look for `.value` because this means the user selected an
  // autocompleted value and is not currently typing a value. This is important if the commute router
  // is displayed on the same step than the building selector
  const homeAddressAnswer = previousAnswers[REF_KEYS_LIST.accountAddress]?.value;
  const buildingAddressAnswer = getBuildingAnswer(previousAnswers)?.value || enterpriseSurveyData.building?.address && enterpriseSurveyData.building.location;
  // Hide CommuteTimeline when the home address and building address are not defined
  if (!homeAddressAnswer || !buildingAddressAnswer) {
    return false;
  }
  return true;
};
const resolveEnterpriseId = (previousAnswers: Record<string, any>, enterpriseSurveyData: IEnterpriseSurvey | undefined) => previousAnswers[REF_KEYS_LIST.accountEnterpriseId]?.key || enterpriseSurveyData?.enterprise?.id || enterpriseSurveyData?.accountContext?.enterpriseId;
const filterWorksiteSelector = (answersById: Record<string, any> | undefined, enterpriseSurveyData: IEnterpriseSurvey | undefined) => {
  // Hide WorksiteSelector when no enterprise is available
  if (!resolveEnterpriseId(answersById || {}, enterpriseSurveyData)) {
    return false;
  }
  // Hide WorksiteSelector when the worksite is already preset via the short link
  if (enterpriseSurveyData?.worksite?.id) {
    return false;
  }
  return true;
};
const filterBuildingSelector = (answersById: Record<string, any> | undefined, enterpriseSurveyData: IEnterpriseSurvey | undefined) => {
  // Hide BuildingSelector when no enterprise is available
  if (!resolveEnterpriseId(answersById || {}, enterpriseSurveyData)) {
    return false;
  }
  // Hide BuildingSelector when the building is already preset via the short link
  if (enterpriseSurveyData?.building?.id) {
    return false;
  }
  return true;
};
const filterEnterpriseSelector = (enterpriseSurveyData: IEnterpriseSurvey | undefined) => {
  // Hide CompanySelector when enterprise is already preset in the short link
  if (enterpriseSurveyData?.enterprise?.id || enterpriseSurveyData?.accountContext?.enterpriseId) {
    return false;
  }
  return true;
};
const filterSelectableCardsQuestions = (question: Question | undefined) => {
  const options = question?.content?.options?.options;
  if (!options || !Array.isArray(options) || Array.isArray(options) && options.length <= 0) {
    return false;
  }
  return true;
};
const filterDaySelector = (question: Question, answersById: Record<string, any> | undefined) => {
  // This is to filter the case where the remote frequency and commute frequency questions are in the same section.
  // In that case if there is 0 days selected in the commute frequency (as a temporary state of the day picker) we will
  // hide the remote frequency question.
  const previousAnswers = answersById || {};
  const commuteWeeklyFrequency = previousAnswers[REF_KEYS_LIST.commuteProfileFrequency]?.weeklyFrequency;
  return !(question.referenceKey === REF_KEYS_LIST.commuteProfileRemoteFrequency && commuteWeeklyFrequency === 0);
};
const applyFilterRelatedToSpecificQuestionType = (question: Question, answersById: Record<string, any> | undefined, enterpriseSurveyData: IEnterpriseSurvey) => {
  switch (question.type || '') {
    case QUESTION_TYPES.activityMapper:
      return filterActivityMapper(answersById);
    case QUESTION_TYPES.commuteTimeline:
    case QUESTION_TYPES.commuteTimelineDriveAndTransit:
    case QUESTION_TYPES.commuteRouter:
    case QUESTION_TYPES.secondaryCommuteRouter:
      return filterMissingStartOrEndAddress(answersById, enterpriseSurveyData);
    case QUESTION_TYPES.enterpriseSelector:
      return filterEnterpriseSelector(enterpriseSurveyData);
    case QUESTION_TYPES.radioButton:
    case QUESTION_TYPES.checkboxes:
      return filterSelectableCardsQuestions(question);
    case QUESTION_TYPES.daySelector:
      return filterDaySelector(question, answersById);
    case QUESTION_TYPES.worksiteSelector:
      return filterWorksiteSelector(answersById, enterpriseSurveyData);
    case QUESTION_TYPES.buildingSelector:
      return filterBuildingSelector(answersById, enterpriseSurveyData);
    case QUESTION_TYPES.textInput:
    case QUESTION_TYPES.numberInput:
    case QUESTION_TYPES.dropdown:
    case QUESTION_TYPES.gridQuestions:
    case QUESTION_TYPES.addressAutocomplete:
    case QUESTION_TYPES.timeSelector:
    case QUESTION_TYPES.vehicleSelector:
    case QUESTION_TYPES.activitySelector:
    case QUESTION_TYPES.costSelector:
    case QUESTION_TYPES.currencySelector:
    case QUESTION_TYPES.homeAddressAutocomplete:
    case QUESTION_TYPES.statement:
    case undefined:
    default:
      return true;
  }
};

/**
 * Go through every questions passed in parameter to filter those that
 * doesn't fulfil each filter conditions need to avoid crashes on those components
 *
 * @param questions - Survey Question array
 * @param answersById - Survey cumulated answers
 * @param enterpriseSurveyData - Survey enterpriseSurvey data
 */
export const filterInvalidQuestions = (questions: Question[], answersById: Record<string, any> | undefined, enterpriseSurveyData: IEnterpriseSurvey) => questions.filter(question => applyFilterRelatedToSpecificQuestionType(question, answersById, enterpriseSurveyData));

/**
 * This is used to get the frequency and time data from an existing and complete route object.
 */
const extractFrequencyData = (routeData: {
  route: Route;
  frequency: OptionalFrequency;
} | undefined): {
  weeklyFrequency: number;
  days: DaysValues[] | null;
} | null => {
  const {
    route,
    frequency
  } = routeData || {};
  if (!route && !frequency) return null;
  const frequencyData = {
    weeklyFrequency: frequency?.weeklyFrequency || route?.weeklyFrequency || null,
    days: frequency?.days || route?.days as DaysValues[] || null
  };
  if (isNil(frequencyData.weeklyFrequency)) {
    return null;
  }
  return frequencyData as ReturnType<typeof extractFrequencyData>;
};

/**
 * This is used to get the time data from an existing and complete route object. Ie. not from survey answers which has no information
 * about the time into the route object itself.
 */
const extractTimeData = (routeData: {
  route: Route;
} | undefined) => {
  if (!routeData?.route) return {};
  const {
    route
  } = routeData;
  return {
    arriveAtOffice: route?.arriveAtOffice,
    leaveOffice: route?.leaveOffice
  };
};

/**
 * This is used to get the commute costs from an existing and complete route object. Ie. not from survey answers which has no information
 * about the commute costs into the route object itself.
 */
export const extractCommuteCosts = (route: Route, existingCommuteCosts?: Record<string, CommuteCostsProps>) => {
  if (!route?.commuteProfile?.commuteCosts) return existingCommuteCosts || {};
  const getCostUniqueKey = (cost: CommuteCostsProps) => cost.stop || cost.mode || cost.category || 'unknown';
  const uniqueCosts = new Map(Object.values(existingCommuteCosts || {}).map(cost => [getCostUniqueKey(cost), cost]));
  route.commuteProfile?.commuteCosts?.forEach(cost => {
    const uniqueKey = getCostUniqueKey(cost);
    if (!uniqueCosts.has(uniqueKey)) {
      uniqueCosts.set(uniqueKey, cost);
    }
  });
  return uniqueCosts.values().reduce<Record<string, CommuteCostsProps>>((costs, cost) => {
    const uniqueKey = getCostUniqueKey(cost);
    const costKey = `commuteCost_${uniqueKey}`;
    const matchingKey = Object.values(REF_KEYS_LIST).find(key => key.startsWith('commuteCost_') && key === costKey);
    if (matchingKey && cost?.paymentFrequency) {
      return {
        ...costs,
        [matchingKey]: {
          cost: cost.cost,
          currency: 'USD',
          paymentFrequency: cost.paymentFrequency
        }
      };
    }
    return costs;
  }, {});
};
export const extractPeopleCount = (route: Route | undefined, existingPeopleCount?: Record<string, number>): Record<string, number> => {
  const uniquePeopleCount = new Map<string, number>(Object.entries(existingPeopleCount || {}).map(([key, count]) => [key, count]));
  const modeToPeopleCountKey: Record<ModeName, string | null> = {
    [ModeName.CarpoolDriver]: REF_KEYS_LIST.commuteProfilePeopleCountCarpooling,
    [ModeName.CarpoolPassenger]: REF_KEYS_LIST.commuteProfilePeopleCountCarpooling,
    [ModeName.Vanpool]: REF_KEYS_LIST.commuteProfilePeopleCountVanpool,
    [ModeName.Rideshare]: REF_KEYS_LIST.commuteProfilePeopleCountRidesharing,
    [ModeName.Shuttle]: null,
    [ModeName.Bike]: null,
    [ModeName.Walk]: null,
    [ModeName.Remote]: null,
    [ModeName.Carshare]: null,
    [ModeName.None]: null,
    [ModeName.Bikeshare]: null,
    [ModeName.Scooter]: null,
    [ModeName.SharedScooter]: null,
    [ModeName.Bus]: null,
    [ModeName.Train]: null,
    [ModeName.Subway]: null,
    [ModeName.Ferry]: null,
    [ModeName.Tramway]: null,
    [ModeName.OtherTransit]: null,
    [ModeName.Taxi]: null,
    [ModeName.Drive]: null
  };
  route?.legs?.forEach(leg => {
    if (!leg.peopleCount || !leg.mode) {
      return;
    }
    const peopleCountKey = modeToPeopleCountKey[leg.mode];
    if (peopleCountKey && !uniquePeopleCount.has(peopleCountKey)) {
      uniquePeopleCount.set(peopleCountKey, leg.peopleCount);
    }
  });
  return Object.fromEntries(uniquePeopleCount);
};

/**
 * Extracts and processes all route-related data to set survey answers including:
 * - Frequency data (primary and secondary routes)
 * - Time data (arrive/leave office times)
 * - Commute costs
 *
 * @param answers - The survey answers containing route data
 * @returns Object containing enhancedAnswers, frequencyAnswers, timeAnswers, and commuteCosts
 */
export const extractRouteDataAnswers = (answers: Record<string, any>): {
  enhancedAnswers: Record<string, any>;
  frequencyAnswers: {
    [REF_KEYS_LIST.commuteProfileFrequency]?: OptionalFrequency | null;
  };
  timeAnswers: {
    [REF_KEYS_LIST.commuteProfileArrive]?: string | null;
    [REF_KEYS_LIST.commuteProfileLeave]?: string | null;
  };
  commuteCosts: Record<string, CommuteCostsProps>;
  peopleCounts: { [key in ModeName]?: number };
} => {
  if (!answers || typeof answers !== 'object') {
    return {
      enhancedAnswers: {},
      frequencyAnswers: {},
      timeAnswers: {},
      commuteCosts: {},
      peopleCounts: {}
    };
  }
  const routeData = answers[REF_KEYS_LIST.commuteProfileRoute];

  // Extract primary frequency data
  const primaryFrequency = extractFrequencyData(routeData?.primary);
  const secondaryFrequency = extractFrequencyData(routeData?.secondary);
  const frequencyAnswers: {
    [REF_KEYS_LIST.commuteProfileFrequency]?: OptionalFrequency | null;
  } = {};
  if (!isNil(primaryFrequency?.weeklyFrequency)) {
    frequencyAnswers[REF_KEYS_LIST.commuteProfileFrequency] = combineFrequencies([primaryFrequency, secondaryFrequency]);
  }

  // Enhanced answers with frequency data embedded in route objects
  const enhancedAnswers = {
    ...answers
  };
  const updateRouteFrequency = (routeType: 'primary' | 'secondary', frequencyData: {
    weeklyFrequency: number;
    days: DaysValues[] | null;
  } | null) => {
    if (enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute] && typeof enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute] === 'object' && enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute][routeType] && !isNil(frequencyData?.weeklyFrequency)) {
      enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute] = {
        ...enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute],
        [routeType]: {
          ...enhancedAnswers[REF_KEYS_LIST.commuteProfileRoute][routeType],
          frequency: frequencyData
        }
      };
    }
  };
  updateRouteFrequency('primary', primaryFrequency);
  updateRouteFrequency('secondary', secondaryFrequency);
  const timeAnswers = {};
  const primaryTimes = extractTimeData(routeData?.primary);
  if (primaryTimes.arriveAtOffice) {
    timeAnswers[REF_KEYS_LIST.commuteProfileArrive] = primaryTimes.arriveAtOffice;
  }
  if (primaryTimes.leaveOffice) {
    timeAnswers[REF_KEYS_LIST.commuteProfileLeave] = primaryTimes.leaveOffice;
  }
  const primaryRoute = routeData?.primary?.route;
  const secondaryRoute = routeData?.secondary?.route;
  const primaryCommuteCosts = extractCommuteCosts(primaryRoute);
  const commuteCosts = extractCommuteCosts(secondaryRoute, primaryCommuteCosts);
  const primaryPeopleCount = extractPeopleCount(primaryRoute);
  const peopleCounts = extractPeopleCount(secondaryRoute, primaryPeopleCount);
  return {
    enhancedAnswers,
    frequencyAnswers,
    timeAnswers,
    commuteCosts,
    peopleCounts
  };
};